/**
 * demoStore — the invented week, in memory, with the writes that make the
 * walkthrough real.
 *
 * WHY IT HAS TO ACCEPT WRITES. Half the six steps ARE writes: save the drafts,
 * remember a sewer spelling, set a WIP's plan, issue the payslip, mark it
 * paid. A read-only demo would teach the first three steps and stop exactly
 * where somebody new gets nervous. So this holds the same shapes the payroll
 * service holds and mutates them, and every one of them dies with the tab.
 *
 * IT IS NOT A FAKE SHAREPOINT. It does the operations the payroll screens
 * actually perform, in the way they expect, and nothing else — no filtering
 * language, no concurrency, no field validation. A method the screens do not
 * call is a method that is not here, and demoMode's write list makes the
 * absence fail loudly rather than fall through to the real list.
 *
 * RESET IS PART OF THE FEATURE. Somebody learning the screen will save the
 * drafts, get it wrong, and want to start again. `resetDemo()` rebuilds the
 * week from the fixture, which is what the button on Payroll Settings calls.
 */
import type { IPayrollProfile } from "../payrollProfile";
import type { IPayout } from "../payout";
import type { PayoutStatus } from "../payout";
import { totalsOf } from "../payout";
import type { IPayoutLine } from "../payrollMath";
import type { IPayoutSummary } from "../../../lib/sp/SubconPayrollService";
import type { IRateRow } from "../rateCard";
import type { StoredPantsMap } from "../pantsMap";
import type { SewerAliases } from "../sewerAlias";
import type { StoredWipPlans, TagPlan } from "../wipPlan";
import type { StoredWipPants } from "../wipPants";
import { mergeLineEdit, clearPayoutEdits, type StoredLineEdits } from "../lineEdits";
import { mergePayable, type IPayablePass, type StoredKeyPayable } from "../payableQty";
import type { KeyOverride, StoredKeyOverrides } from "../keyOverrides";
import { RATE_CARD } from "../rateCard";
import { buildDemoData, type IDemoData } from "./demoFixture";

const todayIso = (): string => new Date().toISOString().slice(0, 10);

interface IDemoState extends IDemoData {
  /** The day the week was built for, so a tab open past midnight rebuilds. */
  builtFor: string;
  /** Next id handed to a payout created here. */
  nextId: number;
}

let state: IDemoState | null = null;

function ensure(): IDemoState {
  const today = todayIso();
  if (!state || state.builtFor !== today) {
    state = { ...buildDemoData(today), builtFor: today, nextId: 1000 };
  }
  return state;
}

/** Rebuild the invented week from scratch. */
export function resetDemo(): void {
  state = null;
  ensure();
}

/** Everything the payroll hook reads, as one object. */
export function demoData(): IDemoData {
  return ensure();
}

const clone = <T,>(v: T): T => JSON.parse(JSON.stringify(v)) as T;

/**
 * The payroll service's shape, for the methods the screens call.
 *
 * Names and signatures match SubconPayrollService exactly, because the proxy
 * in that file hands calls straight over. Anything missing here that writes is
 * refused by demoMode's list rather than reaching a list.
 */
export const demoPayrollStore = {
  // ─── Profiles ────────────────────────────────────────────────────────────
  async listProfiles(): Promise<IPayrollProfile[]> {
    return clone(ensure().profiles);
  },

  async createProfile(p: IPayrollProfile): Promise<IPayrollProfile> {
    const s = ensure();
    const saved = { ...clone(p), spId: s.nextId++ };
    s.profiles = [...s.profiles, saved];
    return saved;
  },

  async updateProfile(spId: number, p: IPayrollProfile): Promise<void> {
    const s = ensure();
    s.profiles = s.profiles.map(x => (x.spId === spId ? { ...clone(p), spId } : x));
  },

  async setProfileActive(spId: number, active: boolean): Promise<void> {
    const s = ensure();
    s.profiles = s.profiles.map(x => (x.spId === spId ? { ...x, active } : x));
  },

  // ─── Rate card ───────────────────────────────────────────────────────────
  async getRateCard(): Promise<{ card: IRateRow[]; source: "list" | "shipped"; error?: string }> {
    const s = ensure();
    return s.rateCard
      ? { card: clone(s.rateCard), source: "list" }
      : { card: [...RATE_CARD], source: "shipped" };
  },

  async listRateRows(): Promise<{ spId: number; row: IRateRow; active: boolean }[]> {
    const s = ensure();
    return (s.rateCard ?? RATE_CARD).map((row, i) => ({ spId: 500 + i, row: clone(row), active: true }));
  },

  async updateRate(spId: number, patch: { rate?: number | null; item?: string; active?: boolean }): Promise<void> {
    const s = ensure();
    const card = clone(s.rateCard ?? [...RATE_CARD]);
    const i = spId - 500;
    if (i >= 0 && i < card.length) {
      if (patch.rate !== undefined) card[i] = { ...card[i], rate: patch.rate };
      if (patch.item !== undefined) card[i] = { ...card[i], item: patch.item };
    }
    s.rateCard = card;
  },

  // ─── Payouts ─────────────────────────────────────────────────────────────
  async listPayouts(filter?: string): Promise<{ payout: IPayout; snapshotTotal: number | null }[]> {
    const s = ensure();
    // The screens pass one filter shape: payoutRef eq '...'. Anything else
    // returns everything, which is what the caller would get from a list with
    // no matching rows anyway.
    const m = filter ? /payoutRef eq '(.+)'/.exec(filter) : null;
    const want = m ? m[1].replace(/''/g, "'") : null;
    return s.payouts
      .filter(p => !want || p.payoutRef === want)
      .map(p => ({ payout: clone(p), snapshotTotal: null }));
  },

  /**
   * The same rows a live summary read returns: no lines, and the totals the
   * list would have stored. Implemented here rather than left to fall through
   * to the live service, which in demo would read real payouts.
   */
  async listPayoutSummaries(filter?: string): Promise<IPayoutSummary[]> {
    const rows = await this.listPayouts(filter);
    return rows.map(({ payout }) => {
      const t = totalsOf(payout);
      return {
        payout: { ...payout, lines: [] },
        snapshotTotal: t.totalAmount,
        stored: {
          qtyTotal: t.qtyTotal, workTotal: t.workTotal,
          netAmount: t.netAmount, totalAmount: t.totalAmount,
        },
      };
    });
  },

  async readPayoutLinesFor(spIds: readonly number[]): Promise<Map<number, IPayoutLine[]>> {
    const want = new Set(spIds);
    const out = new Map<number, IPayoutLine[]>();
    for (const p of ensure().payouts) {
      if (p.spId !== undefined && want.has(p.spId)) out.set(p.spId, clone(p.lines));
    }
    return out;
  },

  async listPayoutsForPeriod(periodEndIso: string): Promise<IPayout[]> {
    return clone(ensure().payouts.filter(p => p.periodEnd === periodEndIso));
  },

  async listPayoutsSince(periodEndIso: string): Promise<IPayout[]> {
    return clone(ensure().payouts.filter(p => p.periodEnd >= periodEndIso));
  },

  async createPayout(p: IPayout): Promise<IPayout> {
    const s = ensure();
    const saved = { ...clone(p), spId: s.nextId++ };
    s.payouts = [...s.payouts, saved];
    return saved;
  },

  async updatePayout(spId: number, p: IPayout): Promise<void> {
    const s = ensure();
    s.payouts = s.payouts.map(x => (x.spId === spId ? { ...clone(p), spId } : x));
  },

  async setPayoutStatus(spId: number, to: PayoutStatus, by: string, reason?: string): Promise<void> {
    const s = ensure();
    const now = new Date().toISOString();
    s.payouts = s.payouts.map(x => {
      if (x.spId !== spId) return x;
      if (to === "Issued") return { ...x, status: to, issuedBy: by, issuedAt: now };
      if (to === "Paid") return { ...x, status: to, paidBy: by, paidAt: now };
      if (to === "Void") return { ...x, status: to, voidedBy: by, voidedAt: now, voidReason: reason };
      return { ...x, status: to };
    });
  },

  async deleteDraft(payout: IPayout): Promise<void> {
    const s = ensure();
    s.payouts = s.payouts.filter(x => x.spId !== payout.spId);
  },

  // ─── The stored maps ─────────────────────────────────────────────────────
  async getPantsMap(): Promise<StoredPantsMap> {
    return { ...ensure().pantsMap };
  },

  async setPantsChoice(design: string, pantsRateKey: string, by: string): Promise<StoredPantsMap> {
    void by;
    const s = ensure();
    s.pantsMap = { ...s.pantsMap, [design]: pantsRateKey };
    return { ...s.pantsMap };
  },

  async clearPantsChoice(design: string, by: string): Promise<StoredPantsMap> {
    void by;
    const s = ensure();
    const next = { ...s.pantsMap };
    delete next[design];
    s.pantsMap = next;
    return { ...next };
  },

  async getSewerAliases(): Promise<SewerAliases> {
    return { ...ensure().aliases };
  },

  async setSewerAlias(alias: string, ledgerKey: string, by: string): Promise<SewerAliases> {
    void by;
    const s = ensure();
    s.aliases = { ...s.aliases, [alias.toUpperCase().trim()]: ledgerKey.toUpperCase().trim() };
    return { ...s.aliases };
  },

  async getWipPlans(): Promise<StoredWipPlans> {
    return { ...ensure().wipPlans };
  },

  async setWipPlan(wipId: string, plan: TagPlan, by: string): Promise<StoredWipPlans> {
    void by;
    const s = ensure();
    s.wipPlans = { ...s.wipPlans, [String(wipId).trim()]: plan };
    return { ...s.wipPlans };
  },

  async getWipPants(): Promise<StoredWipPants> {
    return { ...ensure().wipPants };
  },

  async setWipPants(wipId: string, rateKey: string, by: string): Promise<StoredWipPants> {
    void by;
    const s = ensure();
    s.wipPants = { ...s.wipPants, [String(wipId).trim()]: String(rateKey).trim() };
    return { ...s.wipPants };
  },

  async clearWipPants(wipId: string, by: string): Promise<StoredWipPants> {
    void by;
    const s = ensure();
    const next = { ...s.wipPants };
    delete next[String(wipId).trim()];
    s.wipPants = next;
    return { ...next };
  },

  async getLineEdits(): Promise<StoredLineEdits> {
    return { ...ensure().lineEdits };
  },

  async setLineEdit(
    ref: string, derived: { rateKey: string; rate: number; qty: number },
    change: { rate?: number; qty?: number }, by: string,
  ): Promise<StoredLineEdits> {
    void by;
    const s = ensure();
    s.lineEdits = mergeLineEdit(s.lineEdits, String(ref).trim(), derived, change);
    return { ...s.lineEdits };
  },

  async clearLineEdits(ref: string, by: string): Promise<StoredLineEdits> {
    void by;
    const s = ensure();
    s.lineEdits = clearPayoutEdits(s.lineEdits, String(ref).trim());
    return { ...s.lineEdits };
  },

  async getKeyPayable(): Promise<StoredKeyPayable> {
    return { ...ensure().keyPayable };
  },

  async setKeyPayable(
    passes: readonly IPayablePass[], alloc: Readonly<Record<string, number>>, by: string,
  ): Promise<StoredKeyPayable> {
    void by;
    const s = ensure();
    s.keyPayable = mergePayable(s.keyPayable, passes, alloc);
    return { ...s.keyPayable };
  },

  async getKeyOverrides(): Promise<StoredKeyOverrides> {
    return { ...ensure().keyOverrides };
  },

  async setKeyOverride(key: string, override: KeyOverride, by: string): Promise<StoredKeyOverrides> {
    void by;
    const s = ensure();
    s.keyOverrides = { ...s.keyOverrides, [String(key).trim()]: override };
    return { ...s.keyOverrides };
  },

  async clearKeyOverride(key: string, by: string): Promise<StoredKeyOverrides> {
    void by;
    const s = ensure();
    const next = { ...s.keyOverrides };
    delete next[String(key).trim()];
    s.keyOverrides = next;
    return { ...next };
  },

  async setKeyOverrides(keys: readonly string[], override: KeyOverride, by: string): Promise<StoredKeyOverrides> {
    void by;
    const s = ensure();
    const next = { ...s.keyOverrides };
    for (const k of keys) { const t = String(k).trim(); if (t) next[t] = override; }
    s.keyOverrides = next;
    return { ...next };
  },

  async clearKeyOverrides(keys: readonly string[], by: string): Promise<StoredKeyOverrides> {
    void by;
    const s = ensure();
    const next = { ...s.keyOverrides };
    for (const k of keys) delete next[String(k).trim()];
    s.keyOverrides = next;
    return { ...next };
  },

  async clearWipPlan(wipId: string, by: string): Promise<StoredWipPlans> {
    void by;
    const s = ensure();
    const next = { ...s.wipPlans };
    delete next[String(wipId).trim()];
    s.wipPlans = next;
    return { ...next };
  },
};

export type DemoPayrollStore = typeof demoPayrollStore;
